import React, { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import withApiData from '../HOC/withApiData'
import colors from '../../configuration/colors'
import { hideModal } from '../../actions/modal'
import { createNewCourseJourney } from '../mycourses/Journeys/API'
import Modal from '../mini/Modal'
import Button from '../mini/Buttons/Button'
import styled from 'styled-components'
import H2 from '../typography/headers/H2'
import MarginRight15 from '../typography/margins/MarginRight15'
import CancelButton from '../mini/Buttons/CancelButton'
import ReactTooltip from 'react-tooltip'
import TextInputComponent from './CreateOrEditCourseModal/InputComponents/TextInputComponent'
import { italicsAndBoldOnlyMDEOptions } from '../../utils/markdowneditor'
import {cloneCourse} from '../mycourses/API'

const ModalContainer = styled.div`
  width: 660px;
  border-radius: 10px;
  box-shadow: 0 2px 3.8px 0.2px rgba(0, 0, 0, 0.15);
  background-color: ${colors.smoke};
  margin: 180px auto;
`

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const HeaderContainer = styled.div`
  height: 80px;
  width: auto;
  border-radius: 10px 10px 0 0;
  background-color: ${colors.white};
  border-bottom: 1px solid ${colors.rock};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 30px;
`

const MainContainer = styled.div`
  padding: 10px 30px 30px;
`

const CreateJourneyModal = ({
  apiData,
  activityCourseId,
  selectedAcademicYearId,
  handleDisplayJourneysListOnCreation,
  selectedJourney,
  color
}) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const [isMutating, setIsMutating] = useState(false)

  const closeTheModal = () => {
    setTimeout(() => {
      dispatch(hideModal())
    }, 300)
  }

  const [journeyName, setJourneyName] = useState(selectedJourney ? `Copy of ${selectedJourney.name}` : '')
  const handleJourneyName = e => {
    setJourneyName(e.target.value)
  }

  const [journeyDescription, setJourneyDescription] = useState(selectedJourney ? selectedJourney.description : '')
  const handleJourneyDescription = e => {
    let event = {
      target: {}
    }
    event.target.id = 'journey-description'
    event.target.value = e

    setJourneyDescription(event.target.value)
  }

  const createMutation = useMutation(
    ({ activityCourseId, journeyName, journeyDescription }) =>
      createNewCourseJourney(
        apiData,
        activityCourseId,
        journeyName,
        journeyDescription
      )
  )
  const cloneMutation = useMutation(
    ({ apiData, activityCourseId, selectedJourneyId, journeyName, journeyDescription, color }) =>
      cloneCourse(
        apiData,
        activityCourseId,
        selectedJourneyId,
        journeyName,
        journeyDescription,
        color
      )
  )
  const couldSaveData = journeyName.length > 0

  const handleSaveJourney = () => {
    if (couldSaveData) {
      if (selectedJourney) {
        setIsMutating(true)
        cloneMutation.mutate(
          {
            apiData,
            activityCourseId,
            selectedJourneyId: selectedJourney.id,
            journeyName,
            journeyDescription,
            color
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries(`activity-course-journeys-${activityCourseId}`)
              queryClient.invalidateQueries(
                'mentor-master-courses-' + selectedAcademicYearId
              )
              setTimeout(() => {
                closeTheModal()
                setIsMutating(false)
              }, 300)
            }
          }
        )
      } else {
        setIsMutating(true)
        createMutation.mutate(
          {
            apiData,
            activityCourseId,
            journeyName,
            journeyDescription
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries(
                'activity-course-journeys-' + activityCourseId
              )
              queryClient.invalidateQueries(
                'mentor-master-courses-' + selectedAcademicYearId
              )
              setTimeout(() => {
                closeTheModal()
                setIsMutating(false)
              }, 300)
            }
          }
        )
      }
      handleDisplayJourneysListOnCreation()
    }
  }

  const journeyDescriptionOptions = Object.assign(
    {},
    italicsAndBoldOnlyMDEOptions
  )
  journeyDescriptionOptions.initialValue = journeyDescription
  journeyDescriptionOptions.placeholder = 'Journey Description'
  journeyDescriptionOptions.autofocus = false

  return (
    <Modal
      width={1400}
      percentHeight={100}
      background={'rgba(36,54,70,0.48)'}
      lockBodyScroll={false}
      opacity={'.9'}
      allowInnerScroll
      allowCloseOnExternalClick={false}
    >
      <ModalContainer>
        <HeaderContainer>
          <H2>+ New Journey</H2>
          <FlexContainer>
            <MarginRight15>
              <CancelButton
                handleClick={closeTheModal}
                title="Cancel"
                width="104px"
              />
            </MarginRight15>
            <div
              data-for="createCourseTooltip"
              data-tip="Please enter all required fields prior to creating this course."
            >
              <Button
                width="104px"
                title="Create"
                handleClick={handleSaveJourney}
                buttonColor={colors.oeYellow}
                borderColor={colors.oeYellow}
                hoverBackground={colors.charcoal4}
                hoverColor={colors.white}
                couldSaveData={couldSaveData}
                shouldShowTooltipAndDisableButton={true}
                isMutating={isMutating}
              />
              {!couldSaveData && (
                <ReactTooltip
                  id="createCourseTooltip"
                  place="bottom"
                  effect="solid"
                  className="activityCourseTooltip"
                />
              )}
            </div>
          </FlexContainer>
        </HeaderContainer>
        <MainContainer>
          <TextInputComponent
            title="Journey Name"
            placeholder="Journey Name"
            handleEnteredTextValue={handleJourneyName}
            value={journeyName}
            shouldDisplayAsteriskSymbol={true}
          />
          <TextInputComponent
            className="journey-description"
            title="Journey Description"
            placeholder="Journey Description"
            handleEnteredTextValue={handleJourneyDescription}
            hintText="A basic overview (2-5 sentences) of the journey, including its overarching theme and intended outcomes (both skill and content)."
            value={journeyDescription}
            shouldDisplayAsteriskSymbol={false}
            shouldBeTextarea={true}
            options={journeyDescriptionOptions}
          />
        </MainContainer>
      </ModalContainer>
    </Modal>
  )
}

CreateJourneyModal.propTypes = {
  apiData: PropTypes.object.isRequired,
  activityCourseId: PropTypes.number.isRequired,
  selectedAcademicYearId: PropTypes.number.isRequired,
  handleDisplayJourneysListOnJourneyCreation: PropTypes.func.isRequired
}

export default withApiData(CreateJourneyModal)



// WEBPACK FOOTER //
// ./src/components/modal/CreateJourneyModal.js